import { Container, Graphics, Text, type TextStyle } from 'pixi.js';
import type { Vec2 } from './physics.js';
import { dirToAngle, angleToDir } from './physics.js';
import { drawBoatPerson } from './person.js';

const HULL_LENGTH = 30;
const HULL_WIDTH  = 14;
const MAST_Y      = -8; // mast position along boat axis (slightly forward of center)

export const BOAT_SHAPES = ['classic', 'wide', 'sleek', 'fishing'] as const;
export type BoatShape = typeof BOAT_SHAPES[number];

/** Bow-to-stern outline for each hull shape, local space (bow = -y). */
function hullPoints(shape: BoatShape): { x: number; y: number }[] {
  switch (shape) {
    case 'wide':
      // Shorter and beamier — a stubby little tub of a boat.
      return [{ x: 0, y: -26 }, { x: 10, y: 16 }, { x: -10, y: 16 }];
    case 'sleek':
      // Longer and narrower — a racing hull.
      return [{ x: 0, y: -34 }, { x: 5, y: 14 }, { x: -5, y: 14 }];
    case 'fishing':
      // Squared-off stern instead of a point.
      return [
        { x: 0, y: -24 }, { x: 8, y: -4 }, { x: 8, y: 16 },
        { x: -8, y: 16 }, { x: -8, y: -4 },
      ];
    case 'classic':
    default:
      return [
        { x: 0, y: -HULL_LENGTH },
        { x: HULL_WIDTH * 0.5, y: HULL_LENGTH * 0.5 },
        { x: -HULL_WIDTH * 0.5, y: HULL_LENGTH * 0.5 },
      ];
  }
}

const NAME_STYLE: Partial<TextStyle> = {
  fontFamily: 'system-ui, sans-serif',
  fontSize: 11,
  fill: 0xffffff,
  align: 'center',
};

export class Boat {
  container: Container;
  private hull: Graphics;
  private sail: Graphics;
  private wake: Graphics;
  private person: Graphics;
  private nameLabel: Text;

  constructor(
    hullColor: number = 0xe8d5a3, shirtColor: number = 0x2255aa, hairStyle: number = 1, name: string = '',
    shape: BoatShape = 'classic', hairColor?: number,
  ) {
    this.container = new Container();
    this.wake = new Graphics();
    this.hull = new Graphics();
    this.person = new Graphics();
    this.sail = new Graphics();
    this.nameLabel = new Text({ text: name, style: NAME_STYLE });
    this.nameLabel.anchor.set(0.5, 1);
    this.nameLabel.position.set(0, -HULL_LENGTH - 6);

    this.container.addChild(this.wake);
    this.container.addChild(this.hull);
    this.container.addChild(this.person);
    this.container.addChild(this.sail);
    this.container.addChild(this.nameLabel);

    this.drawHull(hullColor, shape);
    this.drawPerson(shirtColor, hairStyle, hairColor);
  }

  private drawHull(color: number, shape: BoatShape): void {
    const g = this.hull;
    g.clear();
    const pts = hullPoints(shape);

    // Hull shadow
    g.moveTo(pts[0].x + 2, pts[0].y + 2);
    for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x + 2, pts[i].y + 2);
    g.closePath();
    g.fill({ color: 0x000000, alpha: 0.2 });

    // Hull body (bow = north in local space)
    g.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
    g.closePath();
    g.fill({ color });
    g.stroke({ color: 0x8b6914, width: 2 });

    // Mast dot
    g.circle(0, MAST_Y, 2.5);
    g.fill({ color: 0x5a3a10 });
  }

  private drawPerson(shirtColor: number, hairStyle: number, hairColor?: number): void {
    this.person.position.set(0, HULL_LENGTH * 0.35);
    drawBoatPerson(this.person, shirtColor, hairStyle, hairColor);
  }

  setPersonVisible(visible: boolean): void {
    this.person.visible = visible;
  }

  update(boatDir: Vec2, sailDir: Vec2, speed: number): void {
    // Rotate container so bow faces boatDir
    this.container.rotation = dirToAngle(boatDir);

    // Keep person and name facing screen-up regardless of boat rotation
    this.person.rotation = -this.container.rotation;
    this.nameLabel.rotation = -this.container.rotation;

    // Boom direction in boat-local space:
    // compute compass angle of boom relative to boat heading, then convert to local vector.
    // angleToDir(compass) gives the screen-space direction for that compass bearing,
    // which is exactly the Pixi local-space vector we need since the container is already rotated.
    const localCompassAngle = dirToAngle(sailDir) - dirToAngle(boatDir);
    const boomLocal = angleToDir(localCompassAngle);

    const s       = this.sail;
    const sailLen = 22;
    const sx      = boomLocal.x * sailLen;
    const sy      = boomLocal.y * sailLen;

    s.clear();
    // Boom line from mast
    s.moveTo(0, MAST_Y);
    s.lineTo(sx, MAST_Y + sy);
    s.stroke({ color: 0xfff8e7, width: 3, cap: 'round' });

    // Sail fill — thin triangle from mast to boom tip
    s.moveTo(0, MAST_Y - 10);
    s.lineTo(0, MAST_Y);
    s.lineTo(sx, MAST_Y + sy);
    s.closePath();
    s.fill({ color: 0xfffff0, alpha: 0.6 });

    // Wake
    const w = this.wake;
    w.clear();
    if (speed > 0.3) {
      const wakeLen = Math.min(speed * 7, 50);
      const wakeAlpha = Math.min(speed / 3, 0.55);

      w.moveTo(-6, HULL_LENGTH * 0.5);
      w.lineTo(-wakeLen * 0.7, HULL_LENGTH * 0.5 + wakeLen * 1.4);
      w.stroke({ color: 0x7dd8ee, alpha: wakeAlpha, width: 1.5, cap: 'round' });

      w.moveTo(6, HULL_LENGTH * 0.5);
      w.lineTo(wakeLen * 0.7, HULL_LENGTH * 0.5 + wakeLen * 1.4);
      w.stroke({ color: 0x7dd8ee, alpha: wakeAlpha, width: 1.5, cap: 'round' });
    }
  }
}
